/**
 * Desktop mega menu: left column = child links; right = product grid per hovered row.
 * Panels use data-hover-panel-slot (Highlight N = Nth child) when no handle is set,
 * or data-hover-panel-for when the theme setting "Child link handle" is filled.
 */
function normalizeHandle(value) {
  return (value || '').toString().trim().toLowerCase();
}

function panelMatchesNav(panel, navSlot, navHandle) {
  const forH = normalizeHandle(panel.dataset.hoverPanelFor);
  const ps = panel.dataset.hoverPanelSlot;
  const nh = normalizeHandle(navHandle);
  const slotMatch = String(ps || '') === String(navSlot || '');
  if (forH !== '') {
    if (forH === nh) return true;
    /* Row handle in theme doesn’t match nav handle (normalization / wrong field): still map row N → slot N */
    return slotMatch;
  }
  return slotMatch;
}

/**
 * Theme measures submenu height once on open; Fiid hover blocks grow when images
 * and titles layout. Keep header --submenu-height / --full-open-header-height in
 * sync so clip-path and transforms do not cut off product text.
 */
function bindSubmenuHeightSync(layout) {
  if (layout.dataset.fiidSubmenuHeightSync === 'true') return;
  layout.dataset.fiidSubmenuHeightSync = 'true';

  const headerEl = () => /** @type {HTMLElement | null} */ (document.querySelector('#header-component'));

  const sync = () => {
    const header = headerEl();
    const submenu = /** @type {HTMLElement | null} */ (layout.closest('.menu-list__submenu'));
    if (!header || !submenu || !submenu.hasAttribute('data-active')) return;

    const h = Math.ceil(submenu.offsetHeight);
    if (h < 1) return;

    header.style.setProperty('--submenu-height', `${h}px`);

    const isOverlap = header.hasAttribute('data-submenu-overlap-bottom-row');
    const headerVisibleHeight =
      isOverlap && header.offsetHeight > 0
        ? /** @type {HTMLElement | null} */ (header.querySelector('.header__row--top'))?.offsetHeight ?? 0
        : header.offsetHeight;

    header.style.setProperty('--full-open-header-height', `${h + (headerVisibleHeight ?? 0)}px`);
  };

  let frame = 0;
  /** Throttle height sync — rapid layout from images/panels was fighting clip-path and caused flicker */
  const rafSync = () => {
    cancelAnimationFrame(frame);
    frame = requestAnimationFrame(() => sync());
  };

  let roIdle = 0;
  const ro = new ResizeObserver(() => {
    clearTimeout(roIdle);
    roIdle = setTimeout(() => rafSync(), 32);
  });
  ro.observe(layout);

  layout.querySelectorAll('img').forEach((img) => {
    if (!img.complete) img.addEventListener('load', rafSync, { once: true });
  });

  rafSync();
}

function countHoverProductItems(panel) {
  return panel.querySelectorAll('.mega-menu__hover-product-item').length;
}

function initLayout(layout) {
  if (layout.dataset.fiidHoverBound === 'true') return;
  layout.dataset.fiidHoverBound = 'true';

  bindSubmenuHeightSync(layout);

  const navLinks = [...layout.querySelectorAll('[data-hover-nav-slot]')];
  const panels = [...layout.querySelectorAll('.mega-menu__hover-panel[data-hover-panel-slot]')];
  const defaultPanel = layout.querySelector('[data-hover-panel-default]');

  function setActiveNav(activeSlot) {
    const s = String(activeSlot ?? '');
    navLinks.forEach((a) => {
      a.classList.toggle('is-active', String(a.dataset.hoverNavSlot || '') === s);
    });
  }

  /** Coalesce rapid pointer moves across nav rows into one layout pass */
  let activateRaf = 0;
  let pendingSlot = null;
  let pendingHandle = null;

  function activate(navSlot, navHandle) {
    /** @type {HTMLElement | null} */
    let chosen = null;

    panels.forEach((panel) => {
      if (panelMatchesNav(panel, navSlot, navHandle)) {
        chosen = panel;
      }
    });

    if (chosen && countHoverProductItems(chosen) === 0) {
      chosen = null;
    }

    const defaultCount = defaultPanel ? countHoverProductItems(defaultPanel) : 0;
    if (!chosen && defaultCount === 0 && panels.length > 0) {
      chosen = panels.find((p) => countHoverProductItems(p) > 0) ?? null;
    }

    const showDefault = !chosen;

    panels.forEach((panel) => {
      panel.hidden = showDefault || panel !== chosen;
    });

    if (defaultPanel) {
      defaultPanel.hidden = !showDefault;
    }

    setActiveNav(navSlot);
  }

  function queueActivate(navSlot, navHandle) {
    pendingSlot = navSlot;
    pendingHandle = navHandle;
    if (activateRaf) return;
    activateRaf = requestAnimationFrame(() => {
      activateRaf = 0;
      if (pendingSlot == null) return;
      const s = pendingSlot;
      const h = pendingHandle ?? '';
      pendingSlot = null;
      pendingHandle = null;
      activate(s, h);
    });
  }

  navLinks.forEach((a) => {
    const run = () => queueActivate(a.dataset.hoverNavSlot, a.dataset.hoverNavHandle || '');
    a.addEventListener('pointerenter', run);
    a.addEventListener('focus', run);
  });

  const first = navLinks[0];
  const runInitialActivate = () => {
    if (first) {
      activate(first.dataset.hoverNavSlot, first.dataset.hoverNavHandle || '');
    } else if (defaultPanel) {
      defaultPanel.hidden = false;
    }
  };

  runInitialActivate();

  /* Submenu may mount before it’s measurable/active; re-sync when it opens */
  const submenu = layout.closest('.menu-list__submenu');
  if (submenu) {
    let replayTimer = 0;
    const replay = () => {
      if (!submenu.hasAttribute('data-active')) return;
      const active = layout.querySelector('.mega-menu__hover-nav-link.is-active');
      const link = active || first;
      if (link) activate(link.dataset.hoverNavSlot, link.dataset.hoverNavHandle || '');
      else runInitialActivate();
    };
    const scheduleReplay = () => {
      clearTimeout(replayTimer);
      replayTimer = setTimeout(replay, 40);
    };
    const mo = new MutationObserver(() => scheduleReplay());
    mo.observe(submenu, { attributes: true, attributeFilter: ['data-active'] });
    replay();
  }
}

function scan(root = document) {
  root.querySelectorAll('.mega-menu__hover-layout').forEach(initLayout);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => scan());
} else {
  scan();
}

document.addEventListener('shopify:section:load', (event) => {
  if (event.target) scan(event.target);
});
